/**
 * Centralized Error Handling Utility
 * Provides consistent error handling across the application
 */

export interface ErrorDetails {
  message: string;
  code?: string;
  statusCode?: number;
  field?: string;
  originalError?: unknown;
}

/**
 * Extract user-friendly error message from various error types
 */
export const getErrorMessage = (error: unknown): string => {
  // Handle Error instances
  if (error instanceof Error) {
    return error.message;
  }

  // Handle API error responses
  if (typeof error === 'object' && error !== null) {
    const errorObj = error as Record<string, unknown>;
    
    // Laravel validation errors
    if (errorObj.errors && typeof errorObj.errors === 'object') {
      const errors = errorObj.errors as Record<string, string[]>;
      const errorMessages = Object.values(errors).flat();
      return errorMessages.join(', ') || 'Validation failed';
    }

    // API error message
    if (errorObj.message && typeof errorObj.message === 'string') {
      return errorObj.message;
    }

    // Generic error object
    if (errorObj.error && typeof errorObj.error === 'string') {
      return errorObj.error;
    }
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Default fallback
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Extract detailed error information
 */
export const getErrorDetails = (error: unknown): ErrorDetails => {
  const message = getErrorMessage(error);

  // Try to extract additional details
  if (typeof error === 'object' && error !== null) {
    const errorObj = error as Record<string, unknown>;
    
    return {
      message,
      code: errorObj.code as string | undefined,
      statusCode: errorObj.statusCode as number | undefined,
      field: errorObj.field as string | undefined,
      originalError: error,
    };
  }

  return {
    message,
    originalError: error,
  };
};

/**
 * Check if error is a network error
 */
export const isNetworkError = (error: unknown): boolean => {
  if (error instanceof Error) {
    return (
      error.message.includes('Failed to fetch') ||
      error.message.includes('Network error') ||
      error.message.includes('NetworkError') ||
      error.message.includes('timeout') ||
      error.name === 'TypeError'
    );
  }
  return false;
};

/**
 * Check if error is an authentication error
 */
export const isAuthError = (error: unknown): boolean => {
  if (typeof error === 'object' && error !== null) {
    const errorObj = error as Record<string, unknown>;
    return errorObj.statusCode === 401 || errorObj.statusCode === 403;
  }
  
  if (error instanceof Error) {
    return (
      error.message.includes('Unauthorized') ||
      error.message.includes('Invalid credentials') ||
      error.message.includes('authentication')
    );
  }
  
  return false;
};

/**
 * Check if error is a validation error
 */
export const isValidationError = (error: unknown): boolean => {
  if (typeof error === 'object' && error !== null) {
    const errorObj = error as Record<string, unknown>;
    
    // Laravel validation errors
    if (errorObj.errors && typeof errorObj.errors === 'object') {
      return true;
    }
    
    // Status code 422 (Unprocessable Entity)
    if (errorObj.statusCode === 422) {
      return true;
    }
  }
  
  if (error instanceof Error) {
    return error.message.includes('Validation failed') || error.message.includes('validation');
  }
  
  return false;
};

/**
 * Format validation errors for display
 */
export const formatValidationErrors = (error: unknown): Record<string, string[]> => {
  if (typeof error === 'object' && error !== null) {
    const errorObj = error as Record<string, unknown>;
    
    if (errorObj.errors && typeof errorObj.errors === 'object') {
      return errorObj.errors as Record<string, string[]>;
    }
  }
  
  return {};
};

/**
 * Get user-friendly error message based on error type
 */
export const getUserFriendlyMessage = (error: unknown): string => {
  if (isNetworkError(error)) {
    return 'Unable to connect to server. Please check your internet connection and try again.';
  }

  if (isAuthError(error)) {
    return 'Your session has expired. Please log in again.';
  }

  if (isValidationError(error)) {
    const validationErrors = formatValidationErrors(error);
    const errorMessages = Object.values(validationErrors).flat();
    if (errorMessages.length > 0) {
      return errorMessages.join(', ');
    }
    return 'Please check your input and try again.';
  }

  return getErrorMessage(error);
};

/**
 * Log error for debugging 
 */
export const logError = (error: unknown, context?: string): void => {
  if (import.meta.env.DEV) {
    const errorDetails = getErrorDetails(error);
    console.error('Error:', {
      context: context || 'Unknown',
      message: errorDetails.message,
      code: errorDetails.code,
      statusCode: errorDetails.statusCode,
      field: errorDetails.field,
      error: errorDetails.originalError,
    });
  }
  
  // In production, you could send to error tracking service
  // Example: Sentry.captureException(error, { extra: { context } });
};

/**
 * Handle API error with consistent response
 */
export const handleApiError = (error: unknown, context?: string): ErrorDetails => {
  logError(error, context);
  
  return {
    ...getErrorDetails(error),
    message: getUserFriendlyMessage(error),
  };
};

